import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getWarehouseKpis } from '../lib/api';
import { Buildings, Package, Truck, HandGrabbing, ChartBar, Warning, Star } from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

export default function WarehouseKPIs() {
  const [data, setData] = useState({ warehouses: [] });
  const [loading, setLoading] = useState(true);
  const [lookback, setLookback] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getWarehouseKpis({ lookback_days: lookback });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [lookback]);

  const chartData = (data.warehouses || [])
    .filter(w => !w.code.startsWith('T'))
    .slice(0, 6)
    .map(w => ({
      name: w.code,
      Backlog: w.order_backlog,
      Shipped: w.orders_shipped_period,
      Picks: w.picks_period,
      Stock: w.current_stock,
    }));

  return (
    <Layout>
      <div data-testid="warehouse-kpis-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Warehouse KPIs</h1>
            <p className="text-muted-foreground">Side-by-side operational performance comparison</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="overline">Period</label>
            <select
              value={lookback}
              onChange={(e) => setLookback(parseInt(e.target.value))}
              className="px-3 py-2 border border-border font-medium"
              data-testid="lookback-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <h2 className="text-lg font-bold mb-4">Activity Comparison</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" style={{ fontSize: 12 }} />
                <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
                <Legend />
                <Bar dataKey="Backlog" fill="hsl(var(--warning))" />
                <Bar dataKey="Shipped" fill="hsl(var(--success))" />
                <Bar dataKey="Picks" fill="hsl(var(--brand))" />
                <Bar dataKey="Stock" fill="hsl(var(--muted-foreground))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-80 bg-slate-200 animate-pulse"></div>)
          ) : (data.warehouses || [])
              .filter(w => !w.code.startsWith('T'))
              .map(w => (
            <div key={w.warehouse_id} className="bg-card border border-border rounded-lg p-5" data-testid={`kpi-${w.code}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Buildings size={18} weight="bold" className="text-brand" />
                    <span className="font-mono font-bold text-lg">{w.code}</span>
                    {w.is_default && <Star size={14} weight="fill" className="text-amber-500" />}
                  </div>
                  <p className="text-sm text-muted-foreground">{w.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase font-bold">Utilization</p>
                  <p className="text-2xl font-bold">{w.utilization_pct}%</p>
                </div>
              </div>

              <div className="space-y-3">
                <KPIRow icon={Package} label="Current Stock" value={w.current_stock.toLocaleString()} />
                <KPIRow icon={HandGrabbing} label="Order Backlog" value={w.order_backlog} color="text-amber-600" />
                <KPIRow icon={Truck} label="Shipped (period)" value={w.orders_shipped_period} color="text-green-600" />
                <KPIRow icon={ChartBar} label="Picks (period)" value={w.picks_period} />
                <KPIRow icon={ChartBar} label="Turnover Ratio" value={w.turnover_ratio} />
                <KPIRow icon={Warning} label="Low Stock Items" value={w.low_stock_count} color={w.low_stock_count > 0 ? 'text-red-600' : ''} />
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Locations: {w.locations_count}</span>
                  <span className="font-mono">{w.total_occupancy} / {w.total_capacity} cap</span>
                </div>
                <div className="h-1.5 bg-muted mt-2">
                  <div
                    className="h-full bg-brand transition-all"
                    style={{ width: `${Math.min(w.utilization_pct, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function KPIRow({ icon: Icon, label, value, color = 'text-foreground' }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-muted-foreground">
        <Icon size={14} /> {label}
      </span>
      <span className={`font-mono font-bold ${color}`}>{value}</span>
    </div>
  );
}
